import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatApiService } from '../services/chatApi';
import { getToolMessage, getGeneralStatusMessage } from '../utils/toolMessages';
import { StreamChunk, ToolCall } from '../types/chat';

interface Message {
  id: string;
  type: 'user' | 'assistant' | 'tool_announcement' | 'system';
  content: string;
  timestamp: Date;
  toolsUsed?: ToolCall[];
  status?: 'sending' | 'sent' | 'failed';
  conversationStep?: string;
  prompt?: string;
  structured_data?: any; // ✅ ONLY ADD: For structured results
  presentation_type?: string; // ✅ ONLY ADD: To identify structured content
}

interface ChatState {
  messages: Message[];
  isLoading: boolean;
  currentStatus?: string;
  currentTool?: string;
  error?: string;
  apiHealthy: boolean;
  sessionId?: string;
  conversationStarted: boolean;
  currentStep?: string;
  languageSwitchNotified?: boolean;
  responseComplete: boolean;
}

export const useStreamingChat = () => {
  const { t, i18n } = useTranslation();
  
  // Track initial language when hook mounts
  const initialLanguageRef = useRef(i18n.language);
  
  const [state, setState] = useState<ChatState>({
    messages: [],
    isLoading: false,
    apiHealthy: false,
    sessionId: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    conversationStarted: false,
    languageSwitchNotified: false,
    responseComplete: false
  });

  const chatService = new ChatApiService();

  // Check API health on mount and create initial greeting
  useEffect(() => {
    const checkHealth = async () => {
      const healthy = await chatService.healthCheck();
      setState(prev => ({ ...prev, apiHealthy: healthy }));
      
      if (healthy) {
        // Add initial greeting message with current language
        const greetingMessage: Message = {
          id: 'greeting',
          type: 'assistant',
          content: t('initialMessage'),
          timestamp: new Date()
        };
        setState(prev => ({ ...prev, messages: [greetingMessage] }));
      }
    };
    checkHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - never re-run when language changes

  // Update ONLY the greeting message translation when language changes
  useEffect(() => {
    setState(prev => {
      // Find and update only the greeting message by ID, leave all other messages untouched
      const updatedMessages = prev.messages.map(msg => 
        msg.id === 'greeting' 
          ? { ...msg, content: t('initialMessage') }
          : msg
      );
      
      // Only update state if greeting message was actually found and changed
      const hasGreeting = prev.messages.some(msg => msg.id === 'greeting');
      if (hasGreeting) {
        return { ...prev, messages: updatedMessages };
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]); // Update greeting translation when language changes

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || state.isLoading) return;

    // Use current session ID from state
    let currentSessionId = state.sessionId!;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content,
      timestamp: new Date(),
      status: 'sending'
    };

    setState(prev => ({
      ...prev,
      messages: [...prev.messages, userMessage],
      isLoading: true,
      currentStatus: getGeneralStatusMessage('connecting'),
      error: undefined,
      responseComplete: false
    }));

    try {
      // Use streaming by default to get tool announcements
      let assistantContent = '';
      let toolsUsed: ToolCall[] = [];
      let assistantMessageId = (Date.now() + 1).toString();

      // Mark user message as sent
      setState(prev => ({
        ...prev,
        messages: [...prev.messages.slice(0, -1), { ...userMessage, status: 'sent' }],
        currentStatus: getGeneralStatusMessage('processing'),
        conversationStarted: true
      }));

      // ✅ FIXED: Track whether we've created the assistant message yet
      let assistantMessageCreated = false;

      for await (const chunk of chatService.streamChat(content, currentSessionId)) {
        console.log('🔍 Received chunk:', chunk);
        
        if (chunk.type === 'tool') {
          setState(prev => ({ 
            ...prev, 
            currentTool: getToolMessage(chunk.tool_call?.name || 'Unknown', t),
            currentStatus: undefined
          }));
          
          if (chunk.tool_call) {
            toolsUsed.push(chunk.tool_call);
          }
        } else if (chunk.type === 'tool_announcement') {
          console.log('🎯 Processing tool announcement:', chunk.content);
          
          // ✅ FIXED: Don't add tool announcements as separate messages
          // Instead, update the current status to show in the thinking bubble
          setState(prev => ({
            ...prev,
            currentStatus: chunk.content, // Show in thinking bubble
            currentTool: undefined
          }));
        } 
        // ✅ FIXED: Handle structured summary data with explicit type
        else if (chunk.type === 'summary_data') {
          console.log('🎨 Processing summary data chunk:', chunk.content);
          
          try {
            const structuredData = JSON.parse(chunk.content);
            console.log('🎨 Parsed summary data:', structuredData);
            
            const summaryMessage: Message = {
              id: `summary_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              type: 'assistant',
              content: '', // No text content for structured results
              timestamp: new Date(),
              structured_data: structuredData,
              presentation_type: 'structured_summary'
            };
            
            setState(prev => ({
              ...prev,
              messages: [...prev.messages, summaryMessage],
              currentTool: undefined,
              currentStatus: undefined
            }));
            console.log('🎨 Added summary message');
          } catch (e) {
            console.error('❌ Failed to parse summary data:', e);
            // Fallback to regular token handling
            assistantContent += chunk.content;
            
            // ✅ FIXED: Create assistant message if not created yet
            if (!assistantMessageCreated) {
              const assistantMessage: Message = {
                id: assistantMessageId,
                type: 'assistant',
                content: assistantContent,
                timestamp: new Date(),
                toolsUsed: []
              };
              setState(prev => ({
                ...prev,
                messages: [...prev.messages, assistantMessage]
              }));
              assistantMessageCreated = true;
            } else {
              setState(prev => ({
                ...prev,
                messages: prev.messages.map(msg => 
                  msg.id === assistantMessageId 
                    ? { ...msg, content: assistantContent }
                    : msg
                )
              }));
            }
          }
        }
        // ✅ FIXED: Handle structured search results with explicit type  
        else if (chunk.type === 'search_results') {
          console.log('🎨 Processing search results chunk:', chunk.content);
          
          try {
            const structuredData = JSON.parse(chunk.content);
            console.log('🎨 Parsed search results:', structuredData);
            
            const resultsMessage: Message = {
              id: `results_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              type: 'assistant',
              content: '', // No text content for structured results
              timestamp: new Date(),
              structured_data: structuredData,
              presentation_type: 'structured_results'
            };
            
            setState(prev => ({
              ...prev,
              messages: [...prev.messages, resultsMessage],
              currentTool: undefined,
              currentStatus: undefined
            }));
            console.log('🎨 Added search results message');
          } catch (e) {
            console.error('❌ Failed to parse search results:', e);
            // Fallback to regular token handling
            assistantContent += chunk.content;
            
            // ✅ FIXED: Create assistant message if not created yet
            if (!assistantMessageCreated) {
              const assistantMessage: Message = {
                id: assistantMessageId,
                type: 'assistant',
                content: assistantContent,
                timestamp: new Date(),
                toolsUsed: []
              };
              setState(prev => ({
                ...prev,
                messages: [...prev.messages, assistantMessage]
              }));
              assistantMessageCreated = true;
            } else {
              setState(prev => ({
                ...prev,
                messages: prev.messages.map(msg => 
                  msg.id === assistantMessageId 
                    ? { ...msg, content: assistantContent }
                    : msg
                )
              }));
            }
          }
        }
        else if (chunk.type === 'token') {
          assistantContent += chunk.content;
          
          // ✅ FIXED: Create assistant message only when we get first token
          if (!assistantMessageCreated) {
            const assistantMessage: Message = {
              id: assistantMessageId,
              type: 'assistant',
              content: assistantContent,
              timestamp: new Date(),
              toolsUsed: []
            };
            setState(prev => ({
              ...prev,
              messages: [...prev.messages, assistantMessage]
            }));
            assistantMessageCreated = true;
          } else {
            // Update existing assistant message
            setState(prev => ({
              ...prev,
              messages: prev.messages.map(msg => 
                msg.id === assistantMessageId 
                  ? { ...msg, content: assistantContent }
                  : msg
              ),
              currentStatus: assistantContent ? undefined : getGeneralStatusMessage('compiling')
            }));
          }
        } else if (chunk.type === 'language_detected') {
          const detectedLang = chunk.content;
          const languageChanged = i18n.language !== detectedLang;
          
          // Auto-switch UI language if different
          if (languageChanged) {
            i18n.changeLanguage(detectedLang);
            localStorage.setItem('userLanguage', detectedLang);
          }
          
          // Always show notification on first detection
          const languageNames: { [key: string]: string } = {
            'en': 'English',
            'hr': 'Hrvatski',
            'de': 'Deutsch'
          };
          
          const statusMessage: Message = {
            id: `lang_switch_${Date.now()}`,
            type: 'system',
            content: languageChanged 
              ? `🌍 UI language switched to ${languageNames[detectedLang] || detectedLang}` 
              : `🌍 Language detected: ${languageNames[detectedLang] || detectedLang}`,
            timestamp: new Date()
          };
          
          setState(prev => ({
            ...prev,
            messages: [...prev.messages, statusMessage]
          }));
        } else if (chunk.type === 'summary_element') {
          // Handle streaming summary elements
          const data = JSON.parse(chunk.content);
          const element = data.element;
          const sequence = data.sequence;
          
          // Update or create streaming summary message
          setState(prev => {
            const streamingMsgId = 'streaming_summary';
            const existingMsg = prev.messages.find(m => m.id === streamingMsgId);
            
            // Get existing elements from the message or start with empty array
            const existingElements = existingMsg?.structured_data?.elements || [];
            const updatedElements = [...existingElements, { ...element, sequence }]
              .sort((a, b) => a.sequence - b.sequence);
            
            if (existingMsg) {
              return {
                ...prev,
                messages: prev.messages.map(m =>
                  m.id === streamingMsgId
                    ? { 
                        ...m, 
                        structured_data: { elements: updatedElements },
                        presentation_type: 'streaming_summary'
                      }
                    : m
                )
              };
            } else {
              return {
                ...prev,
                messages: [...prev.messages, {
                  id: streamingMsgId,
                  type: 'assistant',
                  content: '',
                  timestamp: new Date(),
                  structured_data: { elements: [{ ...element, sequence }] },
                  presentation_type: 'streaming_summary'
                }]
              };
            }
          });
        } else if (chunk.type === 'summary_complete') {
          // Finalize streaming summary
          setState(prev => ({
            ...prev,
            messages: prev.messages.map(m =>
              m.id === 'streaming_summary'
                ? { 
                    ...m, 
                    id: `summary_${Date.now()}`, 
                    presentation_type: 'structured_summary' 
                  }
                : m
            ),
            currentStatus: undefined
          }));
        } else if (chunk.type === 'result_element') {
          // Handle streaming result elements
          const data = JSON.parse(chunk.content);
          const element = data.element;
          const sequence = data.sequence;
          
          // Update or create streaming results message
          setState(prev => {
            // Find the most recent incomplete streaming results message
            const incompleteStreamingMsg = prev.messages
              .slice()
              .reverse()
              .find(m => 
                m.presentation_type === 'streaming_results' && 
                m.structured_data?.isComplete !== true
              );
            
            if (incompleteStreamingMsg) {
              // Append to existing incomplete streaming results message
              const existingElements = incompleteStreamingMsg.structured_data?.elements || [];
              const updatedElements = [...existingElements, { ...element, sequence }]
                .sort((a, b) => a.sequence - b.sequence);
              
              return {
                ...prev,
                messages: prev.messages.map(m =>
                  m.id === incompleteStreamingMsg.id
                    ? { 
                        ...m, 
                        structured_data: { elements: updatedElements },
                        presentation_type: 'streaming_results'
                      }
                    : m
                )
              };
            } else {
              // Create new streaming results message with unique ID
              const newStreamingMsgId = `streaming_results_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
              return {
                ...prev,
                messages: [...prev.messages, {
                  id: newStreamingMsgId,
                  type: 'assistant',
                  content: '',
                  timestamp: new Date(),
                  structured_data: { elements: [{ ...element, sequence }] },
                  presentation_type: 'streaming_results'
                }]
              };
            }
          });
        } else if (chunk.type === 'results_complete') {
          // Finalize streaming results - keep presentation_type as streaming_results
          const data = JSON.parse(chunk.content);
          const totalElements = data.total_elements;
          
          setState(prev => {
            // Find the most recent incomplete streaming results message
            const incompleteStreamingMsg = prev.messages
              .slice()
              .reverse()
              .find(m => 
                m.presentation_type === 'streaming_results' && 
                m.structured_data?.isComplete !== true
              );
            
            if (incompleteStreamingMsg) {
              return {
                ...prev,
                messages: prev.messages.map(m =>
                  m.id === incompleteStreamingMsg.id
                    ? { 
                        ...m,
                        structured_data: {
                          ...m.structured_data,
                          isComplete: true,
                          totalElements
                        }
                      }
                    : m
                ),
                currentStatus: undefined
              };
            }
            return prev;
          });
        } else if (chunk.type === 'error') {
          setState(prev => {
            // Finalize any streaming content if error occurs during streaming
            const incompleteStreamingResultsMsg = prev.messages
              .slice()
              .reverse()
              .find(m => 
                m.presentation_type === 'streaming_results' && 
                m.structured_data?.isComplete !== true
              );
            const incompleteStreamingSummaryMsg = prev.messages
              .slice()
              .reverse()
              .find(m => 
                m.presentation_type === 'streaming_summary' && 
                m.structured_data?.isComplete !== true
              );
            
            const messages = prev.messages.map(m => {
              if (incompleteStreamingResultsMsg && m.id === incompleteStreamingResultsMsg.id) {
                return { 
                  ...m, 
                  structured_data: {
                    ...m.structured_data,
                    isComplete: true
                  }
                };
              }
              if (incompleteStreamingSummaryMsg && m.id === incompleteStreamingSummaryMsg.id) {
                return { 
                  ...m, 
                  structured_data: {
                    ...m.structured_data,
                    isComplete: true
                  }
                };
              }
              return m;
            });
            
            return {
              ...prev,
              messages,
              error: chunk.content,
              isLoading: false,
              currentTool: undefined,
              currentStatus: undefined
            };
          });
          return;
        } else if (chunk.type === 'done') {
          // ✅ FIXED: Only update if assistant message exists
          if (assistantMessageCreated) {
            setState(prev => ({
              ...prev,
              messages: prev.messages.map(msg =>
                msg.id === assistantMessageId
                  ? { ...msg, content: assistantContent, toolsUsed }
                  : msg
              ),
              isLoading: false,
              currentTool: undefined,
              currentStatus: undefined,
              responseComplete: true
            }));
          } else {
            // No content was generated, just mark as done
            setState(prev => ({
              ...prev,
              isLoading: false,
              currentTool: undefined,
              currentStatus: undefined,
              responseComplete: true
            }));
          }
          return;
        }
      }

    } catch (error) {
      console.error('Chat error:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        currentTool: undefined,
        currentStatus: undefined,
        error: error instanceof Error ? error.message : 'Failed to send message',
        messages: prev.messages.map((msg, index) => 
          index === prev.messages.length - 2 
            ? { ...msg, status: 'failed' as const }
            : msg
        ).slice(0, -1)
      }));
    }
  }, [state.isLoading, state.sessionId, state.conversationStarted, t]);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: undefined }));
  }, []);

  return {
    messages: state.messages,
    isLoading: state.isLoading,
    currentStatus: state.currentStatus,
    currentTool: state.currentTool,
    error: state.error,
    apiHealthy: state.apiHealthy,
    responseComplete: state.responseComplete,
    sendMessage,
    clearError
  };
}; 